import { useEffect, useMemo, useRef, useState } from 'react';
import StyledMenu from '@shared/components/ui/StyledMenu';
import {
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    Input,
    InputAdornment,
    InputLabel,
    ListItemText,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    useTheme,
} from '@mui/material';
import { useEdges, useNodes, useReactFlow } from 'reactflow';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import AddBoxIcon from '@mui/icons-material/AddBox';
import StyledTextField from '@shared/components/ui/StyledTextField';
import Chip from '@mui/material/Chip';
import HtmlTooltip from '@shared/components/ui/HtmlTooltip';
import SimpleAlert from '@shared/components/modals/SimpleAlert';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { TabContext, TabPanel } from '@mui/lab';
import MemoryIcon from '@mui/icons-material/Memory';
import { useTranslation } from 'react-i18next';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TuneIcon from '@mui/icons-material/Tune';
import FileInput from '@shared/components/others/FileInput/FileInput';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { enforceMinMax, matchesIpRegex, toTitleCase } from '@shared/utils/utilsOld';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BuildIcon from '@mui/icons-material/Build';
import isEqual from 'lodash/isEqual';
import {
    INITIAL_TREX_CONFIG,
    LOGAN_BASED_NODES_LIST,
    NODE_DEFAULT_PARAMS,
    NODE_PARAMS_INTERVAL,
    PPPOE_BASED_NODES_LIST,
    UG_NODES_LIST,
} from '@pages/schemes/constants';
import { makeCorrectLoganBasedNodeRoles } from '@pages/schemes/utils';
import TrexConfigForm from '@pages/library/components/TrexConfigForm';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import NodeTypeSelect from './NodeTypeSelect';
import useDeleteNode from '@pages/schemes/hooks/useDeleteNode';
import ADNodeSelect from '@pages/schemes/components/Flow/ADNodeSelect';
import ADRoleSelector from '@pages/schemes/components/Flow/ADRoleSelector';

const MIN_IPS_NUMBER_FOR_PORT = 1;
const MAX_IPS_NUMBER_FOR_PORT = 100;

export function getNextPortObject(data, handlesPool, ipsValue) {
    const existingPorts = data.ports;

    // add first not existing handle
    for (let i = 0; i < handlesPool.length; i++) {
        const handle = handlesPool[i];

        if (existingPorts.find((ep) => ep['name'] === handle)) {
            if (i === handlesPool.length - 1) {
                return null;
            }

            continue;
        }

        return {
            name: handle,
            ips: ipsValue,
        };
    }
}

const ConfigTabs = styled(Tabs)({
    marginTop: '8px',
    '& .MuiTabs-indicator': {
        height: '1px',
        backgroundColor: '#1890ff',
    },
});

const ConfigTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
    textTransform: 'none',
    minWidth: 110,
    minHeight: '30px',
    height: '30px',
    color: '#1890ff',
    '&:hover': {
        color: '#1890ff',
        opacity: 1,
    },
    '&.Mui-selected': {
        backgroundColor: 'var(--custom-textbox-background-color)',
        borderRadius: '4px 4px 0 0',
        border: '1px solid var(--border-color)',
        color: '#1890ff',
        fontWeight: theme.typography.fontWeightMedium,
    },
    '&.Mui-focusVisible': {
        backgroundColor: '#d1eaff',
    },
}));

const NodeConfigMenu = ({
    anchorEl,
    setAnchorEl,
    nodeId,
    addNewPort,
    removePort,
    updateMemory,
    updateCpu,
    handlesPool,
    internalHandlesPool = [],
    withStaticPorts,
    nodesParamsCustomisable,
    configureOnly,
    ipsReadonly,
    handleUpdateIpsPoolSize,
    handleUpdateUsersPoolSize,
    handleUpdateAuthType,
    updateUserCount,
    updateGroupCount,
    updateWords,
    updateLoganBasedRole,
    updateConfig,
    updateConfigName,
    updateTrexConfig,
}) => {
    const { getNode, setNodes } = useReactFlow();
    const node = useMemo(() => getNode(nodeId), [nodeId, getNode]);
    const [tabValue, setTabValue] = useState(
        configureOnly ? 'system' : UG_NODES_LIST.includes(node.type) ? 'type' : 'ports',
    );
    const theme = useTheme();
    const [errorAlertMessage, setErrorAlertMessage] = useState('');
    const [newIpsValue, setNewIpsValue] = useState(MIN_IPS_NUMBER_FOR_PORT);
    const open = Boolean(anchorEl);
    const nodes = useNodes();
    const { t } = useTranslation();
    const [config, setConfig] = useState(node.data.config ?? '');
    const [configName, setConfigName] = useState(node.data.configName ?? '');
    const [trexConfig, setTrexConfig] = useState(node.data.trexConfig ?? INITIAL_TREX_CONFIG);
    const [memoryValue, setMemoryValue] = useState(node.data.memory ?? 0);
    const [cpuValue, setCpuValue] = useState(node.data.cpu ?? 0);
    const [userCount, setUserCount] = useState(node.data['user_count'] ?? 0);
    const [groupCount, setGroupCount] = useState(node.data['group_count'] ?? 0);
    const [usersPoolSize, setUsersPoolSize] = useState(node.data['users-pool-size'] ?? 0);
    const [windowsVersion, setWindowsVersion] = useState(node.data.version ?? NODE_DEFAULT_PARAMS.windows.version);
    const [adWindowsVersion, setAdWindowsVersion] = useState(node.data.version ?? NODE_DEFAULT_PARAMS.ad.version);
    const [ubuntuVersion, setUbuntuVersion] = useState(node.data.version ?? NODE_DEFAULT_PARAMS.ubuntu.version);
    const [words, setWords] = useState(node.data['words'] || false);
    const [hwId, setHwId] = useState(node.data['hw_id'] || null);
    const [wordsInputValue, setWordsInputValue] = useState('');

    const [ipsPoolSize, setIpsPoolSize] = useState(
        node.data['ips-pool-size'] || NODE_DEFAULT_PARAMS.pppoeserver['ips-pool-size'],
    );
    const [authType, setAuthType] = useState(node.data['auth-type'] || NODE_DEFAULT_PARAMS.pppoeserver['auth-type']);
    const [serversCount, setServersCount] = useState(
        node.data['serversCount'] || NODE_DEFAULT_PARAMS.icap.serversCount,
    );
    const [loganBasedNodeRole, setLoganBasedNodeRole] = useState(
        node.data['role'] || (LOGAN_BASED_NODES_LIST.includes(node.type) && NODE_DEFAULT_PARAMS[node.type].role),
    );

    const canLoganChangeRole =
        (node.type === 'logan' && nodes.find((n) => n.id !== node.id && n.type === 'logan')) ||
        (node.type === 'siem' && nodes.find((n) => n.id !== node.id && n.type === 'siem'));

    const minCpuValue = useMemo(() => {
        if (!nodesParamsCustomisable) {
            return;
        }

        return !PPPOE_BASED_NODES_LIST.includes(node.type) && NODE_PARAMS_INTERVAL[node.type]['cpu'][0];
    }, [node.type, nodesParamsCustomisable]);

    const maxCpuValue = useMemo(() => {
        if (!nodesParamsCustomisable) {
            return;
        }

        return !PPPOE_BASED_NODES_LIST.includes(node.type) && NODE_PARAMS_INTERVAL[node.type]['cpu'][1];
    }, [node.type, nodesParamsCustomisable]);

    const minMemoryValue = useMemo(() => {
        if (!nodesParamsCustomisable) {
            return;
        }

        return !PPPOE_BASED_NODES_LIST.includes(node.type) && NODE_PARAMS_INTERVAL[node.type]['memory'][0];
    }, [node.type, nodesParamsCustomisable]);

    const maxMemoryValue = useMemo(() => {
        if (!nodesParamsCustomisable) {
            return;
        }

        return !PPPOE_BASED_NODES_LIST.includes(node.type) && NODE_PARAMS_INTERVAL[node.type]['memory'][1];
    }, [node.type, nodesParamsCustomisable]);

    useEffect(() => {
        if (hwId !== node.data['hw_id']) {
            node.data['hw_id'] = hwId;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hwId]);

    useEffect(() => {
        if (memoryValue !== node.data.memory && memoryValue) {
            updateMemory(memoryValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoryValue]);

    useEffect(() => {
        if (cpuValue !== node.data.cpu && cpuValue) {
            updateCpu(cpuValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpuValue]);

    useEffect(() => {
        if (words && words.length !== node.data.words.length) {
            updateWords(words);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [words]);

    useEffect(() => {
        if (
            handleUpdateIpsPoolSize &&
            ipsPoolSize !== node.data['ips-pool-size'] &&
            ipsPoolSize !== NODE_DEFAULT_PARAMS.pppoeserver['ips-pool-size'] &&
            node.type === 'pppoeserver'
        ) {
            handleUpdateIpsPoolSize(ipsPoolSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ipsPoolSize]);

    useEffect(() => {
        if (
            handleUpdateUsersPoolSize &&
            usersPoolSize !== node.data['users-pool-size'] &&
            usersPoolSize !== NODE_DEFAULT_PARAMS.radius['users-pool-size']
        ) {
            handleUpdateUsersPoolSize(usersPoolSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersPoolSize]);

    useEffect(() => {
        if (
            updateLoganBasedRole &&
            loganBasedNodeRole !== node.data['role'] &&
            LOGAN_BASED_NODES_LIST.includes(node.type)
        ) {
            updateLoganBasedRole(loganBasedNodeRole);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loganBasedNodeRole]);

    useEffect(() => {
        if (
            handleUpdateAuthType &&
            authType !== node.data['auth-type'] &&
            authType !== NODE_DEFAULT_PARAMS['auth-type']
        ) {
            handleUpdateAuthType(authType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authType]);

    useEffect(() => {
        if (
            serversCount !== node.data.serversCount &&
            serversCount !== NODE_DEFAULT_PARAMS.icap.serversCount &&
            node.type === 'icap'
        ) {
            node.data['serversCount'] = serversCount;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serversCount]);

    useEffect(() => {
        if (
            handleUpdateUsersPoolSize &&
            usersPoolSize !== node.data['users-pool-size'] &&
            usersPoolSize !== NODE_DEFAULT_PARAMS[node.type]['users-pool-size']
        ) {
            handleUpdateUsersPoolSize(usersPoolSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersPoolSize]);

    useEffect(() => {
        if (groupCount !== node.data['group_count'] && groupCount) {
            updateGroupCount(groupCount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupCount]);

    useEffect(() => {
        if (userCount !== node.data['user_count'] && userCount) {
            updateUserCount(userCount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCount]);

    const handleMemorySliderChange = (_event, newValue) => {
        setMemoryValue(newValue);
    };

    useEffect(() => {
        if (ipsReadonly) {
            setServersCount(0);
            setIpsPoolSize(1);
        }
    }, [ipsReadonly]);

    const handleMemoryInputChange = (event) => {
        const value = event.target.value;
        setMemoryValue(value === '' ? '' : Number(value));
    };

    const handleMemoryBlur = (event) => {
        let value = memoryValue;

        if (value === '' || value < minMemoryValue) {
            value = minMemoryValue;
        } else if (value > maxMemoryValue) {
            value = maxMemoryValue;
        }

        setMemoryValue(value);
    };

    const handleCpuSliderChange = (_event, newValue) => {
        setCpuValue(newValue);
    };

    const handleCpuInputChange = (event) => {
        const value = event.target.value;
        setCpuValue(value === '' ? '' : Number(value));
    };

    const handleCpuBlur = () => {
        let value = cpuValue;

        if (value === '' || value < minCpuValue) {
            value = minCpuValue;
        } else if (value > maxCpuValue) {
            value = maxCpuValue;
        }

        setCpuValue(value);
    };

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setNodes((nodes) => nodes.map((n) => ({ ...n, selected: false })));
    };

    const handleAddPort = (portObject) => {
        let newHandleObject = portObject || getNextPortObject(node.data, handlesPool, newIpsValue);

        if (newHandleObject) {
            addNewPort(newHandleObject);
            setNewIpsValue(MIN_IPS_NUMBER_FOR_PORT);
        }
    };

    const handleRemovePort = (port) => {
        removePort(port);
    };

    const handleIpsNumberChange = (e) => {
        const inputEl = e.target;
        enforceMinMax(inputEl);

        setNewIpsValue(Number(inputEl.value));
    };

    const handleIpsPoolSizeChange = (e) => {
        setIpsPoolSize(Number(e.target.value));
    };

    const handleUsersPoolSizeChange = (e) => {
        setUsersPoolSize(Number(e.target.value));
    };

    const handleServersCountChange = (e) => {
        setServersCount(Number(e.target.value));
    };

    const handleUserCount = (e) => {
        const newValue = Number(e.target.value);

        if (newValue < groupCount) {
            setGroupCount(newValue);
        }

        setUserCount(newValue);
    };

    const handleGroupCount = (e) => {
        const newValue = Number(e.target.value);

        if (newValue > userCount) {
            setUserCount(newValue);
        }

        setGroupCount(newValue);
    };

    const handleAuthTypeChange = (e) => {
        setAuthType(e.target.value);
    };

    const handleUpdateWindowsVersion = (e) => {
        const value = Number(e.target.value);
        setWindowsVersion(value);
        node.data['version'] = value;
    };

    const handleUpdateAdWindowsVersion = (e) => {
        const value = e.target.value;
        setAdWindowsVersion(value);
        node.data.version = value;
    };
    const handleUpdateUbuntuVersion = (e) => {
        const value = e.target.value;
        setUbuntuVersion(value);
        node.data.version = value;
    };

    const handleLoganBasedNodeRoleChange = (e) => {
        const newRole = e.target.value;

        if (newRole === 'arbitrator') {
            setNodes((nodes) => makeCorrectLoganBasedNodeRoles(nodes, nodeId));
        }

        setLoganBasedNodeRole(newRole);
    };

    const handleSubmitWordsInputValue = (e) => {
        e.preventDefault();
        setWords((prev) => [...prev, ...wordsInputValue.split(',').filter((item) => item.length)]);
        setWordsInputValue('');
    };

    const handleDeleteWord = (wordIndex) => {
        setWords((prev) => prev.filter((_item, index) => wordIndex !== index));
    };

    useEffect(() => {
        if (
            (config !== node.data['config'] && node.type === 'cisco') ||
            node.type === 'windows' ||
            node.type === 'ad' ||
            node.type === 'tech_tour_ad' ||
            node.type === 'encryptor'
        ) {
            updateConfig && updateConfig(config);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config]);

    useEffect(() => {
        if (
            (configName !== node.data['configName'] && node.type === 'cisco') ||
            node.type === 'windows' ||
            node.type === 'ad' ||
            node.type === 'tech_tour_ad' ||
            node.type === 'encryptor'
        ) {
            updateConfigName && updateConfigName(configName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configName]);

    useEffect(() => {
        if (node.type === 'trex' && !isEqual(trexConfig, node.data.trexConfig)) {
            updateTrexConfig && updateTrexConfig(trexConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trexConfig]);

    const handleChangeConfig = (file) => {
        if (file === undefined) return;
        if (file === null) {
            setConfig('');
            setConfigName('');
            return;
        }
        setConfigName(file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
            setConfig(e.target.result);
        };
        reader.readAsText(file);
    };

    return (
        <StyledMenu
            PaperProps={{ sx: { minWidth: '300px' } }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            verticalAnchorOrigin="bottom"
            horizontalAlign="left"
        >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography
                    component="h5"
                    sx={{ textAlign: 'center', margin: '5px', fontWeight: 600, fontSize: '0.8rem' }}
                >
                    {t('node_config_menu', { node: node.type.toUpperCase() })}{' '}
                </Typography>
                {(node.type === 'cisco' || node.type === 'vulnerableapp') && (
                    <Tooltip title={node.type === 'cisco' ? t('cisco_tooltip') : t('vulnerableapp_tooltip')}>
                        <InfoOutlinedIcon sx={{ color: '#1890ff' }} fontSize="small" />
                    </Tooltip>
                )}
            </Box>
            <Box className="reactflow-node-config-menu">
                <TabContext value={tabValue}>
                    <ConfigTabs
                        color="secondary"
                        sx={{ minHeight: '30px', height: '30px' }}
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="fullWidth"
                    >
                        {UG_NODES_LIST.includes(node.type) && (
                            <ConfigTab value="type" icon={<DeviceHubIcon />} iconPosition="start" label={t('type')} />
                        )}
                        <ConfigTab value="ports" icon={<TuneIcon />} iconPosition="start" label={t('ports')} />
                        {nodesParamsCustomisable && !node?.data?.hw_id && (
                            <ConfigTab value="system" icon={<MemoryIcon />} iconPosition="start" label={t('system')} />
                        )}
                        {node.data.words && (
                            <ConfigTab
                                value="words"
                                icon={<BorderColorIcon fontSize="small" />}
                                iconPosition="start"
                                label={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        {t('morphology')}{' '}
                                        <Tooltip title={t('morphology_tooltip')}>
                                            <HelpOutlineIcon fontSize="small" />
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        )}
                        {(node.type === 'cisco' ||
                            node.type === 'windows' ||
                            node.type === 'encryptor' ||
                            node.type === 'ad' ||
                            node.type === 'tech_tour_ad' ||
                            node.type === 'trex') && (
                            <ConfigTab value="config" icon={<BuildIcon />} iconPosition="start" label={t('config')} />
                        )}
                    </ConfigTabs>
                    <TabPanel
                        className="reactflow-node-config-menu__tab-content"
                        value="type"
                        sx={{ p: 0, height: 'max-content', minHeight: 'max-content' }}
                    >
                        <NodeTypeSelect
                            node={node}
                            hwId={hwId}
                            setHwId={setHwId}
                            handleAddPort={handleAddPort}
                            handleRemovePort={handleRemovePort}
                        />
                    </TabPanel>
                    <TabPanel value="ports" sx={{ p: 0 }}>
                        <List
                            dense={true}
                            className="reactflow-node-config-menu__tab-content"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '0 4px 4px 4px',
                                paddingBottom: withStaticPorts ? '5px' : 0,
                                position: 'relative',
                                paddingTop: 0,
                            }}
                        >
                            <ListItem
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    backgroundColor: 'inherit',
                                    zIndex: 111,
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ opacity: 0.7, fontWeight: 900 }}>{t('port_name')}</Box>
                                <Box sx={{ opacity: 0.7, fontWeight: 900 }}>{t('ip_address')}</Box>
                            </ListItem>
                            {node?.data?.ports
                                ?.filter((port) => !internalHandlesPool.includes(port['name']))
                                .slice()
                                .reverse()
                                .map((port) => (
                                    <PortItem
                                        key={port['name']}
                                        port={port}
                                        handleRemovePort={handleRemovePort}
                                        withStaticPorts={withStaticPorts}
                                        configureOnly={configureOnly || Boolean(hwId)}
                                        ipsReadonly={ipsReadonly}
                                        nodeId={nodeId}
                                    />
                                ))}
                            {!withStaticPorts && (
                                <ListItem
                                    sx={{
                                        marginTop: 'auto',
                                        gap: '10px',
                                        position: 'sticky',
                                        bottom: 0,
                                        backgroundColor: 'inherit',
                                        zIndex: 1,
                                    }}
                                    secondaryAction={
                                        <UtmsButton
                                            disabled={configureOnly || Boolean(hwId)}
                                            size="small"
                                            startIcon={<AddBoxIcon />}
                                            onClick={() => handleAddPort()}
                                        >
                                            {t('add_new_port')}
                                        </UtmsButton>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <StyledTextField
                                                sx={{
                                                    backgroundColor: theme.palette.background.paper,
                                                    visibility: 'hidden',
                                                }}
                                                value={newIpsValue}
                                                onChange={handleIpsNumberChange}
                                                size="small"
                                                type="number"
                                                label={t('ips_count')}
                                                InputProps={{
                                                    inputProps: {
                                                        min: MIN_IPS_NUMBER_FOR_PORT,
                                                        max: MAX_IPS_NUMBER_FOR_PORT,
                                                        readOnly: true, // TODO delete when ips are ready on backend
                                                    },
                                                }}
                                            />
                                        }
                                    />
                                </ListItem>
                            )}
                        </List>
                        {Boolean(errorAlertMessage) && (
                            <SimpleAlert
                                severity="error"
                                message={errorAlertMessage}
                                onClose={() => {
                                    setErrorAlertMessage('');
                                }}
                            />
                        )}
                    </TabPanel>
                    {!node?.data?.hw_id && (
                        <TabPanel value="system" sx={{ p: 0 }}>
                            <Box
                                className="reactflow-node-config-menu__tab-content"
                                sx={{
                                    borderRadius: '4px 0 4px 4px',
                                    padding: '16px 8px',
                                }}
                            >
                                {node.type === 'wec' && <ADNodeSelect node={node} field="forest_root_id" fullWidth />}
                                {minMemoryValue && (
                                    <>
                                        {node.type === 'windows' && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <MicrosoftIcon />
                                                    {t('version')}
                                                </Box>
                                                <FormControl>
                                                    <RadioGroup
                                                        row
                                                        value={windowsVersion}
                                                        onChange={handleUpdateWindowsVersion}
                                                    >
                                                        <FormControlLabel
                                                            value={10}
                                                            control={<Radio color="info" />}
                                                            label="10"
                                                        />
                                                        <FormControlLabel
                                                            value={11}
                                                            control={<Radio color="info" />}
                                                            label="11"
                                                        />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        )}
                                        {(node.type === 'ad' ||
                                            node.type === 'tech_tour_ad' ||
                                            node.type === 'isp' ||
                                            node.type === 'dc-a') && (
                                            <>
                                                {(node.type === 'ad' || node.type === 'tech_tour_ad') && (
                                                    <ADRoleSelector node={node} />
                                                )}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <MicrosoftIcon />
                                                        {t('version')}
                                                    </Box>
                                                    <FormControl>
                                                        <RadioGroup
                                                            row
                                                            value={adWindowsVersion}
                                                            onChange={handleUpdateAdWindowsVersion}
                                                        >
                                                            <FormControlLabel
                                                                value={'2019'}
                                                                control={<Radio color="info" />}
                                                                label="2019"
                                                            />
                                                            <FormControlLabel
                                                                value={'2025'}
                                                                control={<Radio color="info" />}
                                                                label="2025"
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Box>
                                            </>
                                        )}
                                        {(node.type === 'ubuntu' ||
                                            node.type === 'cli-a' ||
                                            node.type === 'cli-a-lx' ||
                                            node.type === 'mgt-a' ||
                                            node.type === 'mgt-a-intro') && (
                                            <FormControl sx={{ width: '50%' }} size="small">
                                                <InputLabel id="version-label">{t('version')}</InputLabel>
                                                <Select
                                                    labelId="version-label"
                                                    id="version-select"
                                                    value={ubuntuVersion}
                                                    label={t('version')}
                                                    onChange={handleUpdateUbuntuVersion}
                                                >
                                                    <MenuItem value={'22.04'}>22.04</MenuItem>
                                                    {node.type === 'ubuntu' && (
                                                        <MenuItem value={'22.04 gui'}>22.04 GUI</MenuItem>
                                                    )}
                                                    <MenuItem value={'24.04'}>24.04</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                        {LOGAN_BASED_NODES_LIST.includes(node.type) && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', gap: 1 }}>{`${t('role')}:`}</Box>
                                                <Tooltip
                                                    title={
                                                        !canLoganChangeRole
                                                            ? t('logan_role_hint', {
                                                                  loganType: toTitleCase(node.type),
                                                              })
                                                            : ''
                                                    }
                                                >
                                                    <FormControl disabled={!canLoganChangeRole}>
                                                        <RadioGroup
                                                            row
                                                            value={loganBasedNodeRole}
                                                            onChange={handleLoganBasedNodeRoleChange}
                                                        >
                                                            <FormControlLabel
                                                                value="regular"
                                                                control={<Radio color="info" />}
                                                                label="Regular"
                                                            />
                                                            <FormControlLabel
                                                                value="arbitrator"
                                                                control={<Radio color="info" />}
                                                                label="Arbitrator"
                                                            />
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Tooltip>
                                            </Box>
                                        )}
                                        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                            <Grid item xs={3}>
                                                {t('memory')}:
                                            </Grid>
                                            <Grid item xs={4.5}>
                                                <Slider
                                                    marks={[
                                                        {
                                                            value: minMemoryValue,
                                                            label: `${minMemoryValue} ${t('gb')}`,
                                                        },
                                                        {
                                                            value: maxMemoryValue,
                                                            label: `${maxMemoryValue} ${t('gb')}`,
                                                        },
                                                    ]}
                                                    min={minMemoryValue}
                                                    step={1}
                                                    max={maxMemoryValue}
                                                    color="info"
                                                    size="small"
                                                    value={typeof memoryValue === 'number' ? memoryValue : 0}
                                                    onChange={handleMemorySliderChange}
                                                />
                                            </Grid>
                                            <Grid item xs={3.5}>
                                                <Input
                                                    value={memoryValue}
                                                    size="small"
                                                    onChange={handleMemoryInputChange}
                                                    onBlur={handleMemoryBlur}
                                                    inputProps={{
                                                        step: 1,
                                                        min: minMemoryValue,
                                                        max: maxMemoryValue,
                                                        type: 'number',
                                                    }}
                                                    endAdornment={
                                                        <InputAdornment position="end">{t('gb')}</InputAdornment>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>

                                        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                            <Grid item xs={3}>
                                                {t('cpu')}:
                                            </Grid>
                                            <Grid item xs={4.5}>
                                                <Slider
                                                    marks={[
                                                        {
                                                            value: minCpuValue,
                                                            label: minCpuValue,
                                                        },
                                                        {
                                                            value: maxCpuValue,
                                                            label: maxCpuValue,
                                                        },
                                                    ]}
                                                    min={minCpuValue}
                                                    step={1}
                                                    max={maxCpuValue}
                                                    color="info"
                                                    size="small"
                                                    value={typeof cpuValue === 'number' ? cpuValue : 0}
                                                    onChange={handleCpuSliderChange}
                                                />
                                            </Grid>
                                            <Grid item xs={3.5}>
                                                <Input
                                                    value={cpuValue}
                                                    size="small"
                                                    onChange={handleCpuInputChange}
                                                    onBlur={handleCpuBlur}
                                                    inputProps={{
                                                        step: 1,
                                                        min: minCpuValue,
                                                        max: maxCpuValue,
                                                        type: 'number',
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        {(node.type === 'ad' ||
                                            node.type === 'tech_tour_ad' ||
                                            node.type === 'isp' ||
                                            node.type === 'dc-a') && (
                                            <>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'baseline',
                                                        gap: 2,
                                                    }}
                                                >
                                                    {t('user_count')}:
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        sx={{
                                                            mt: 2,
                                                            ml: 'auto',
                                                            backgroundColor:
                                                                theme.palette.mode === 'light'
                                                                    ? 'white'
                                                                    : 'transparent',
                                                            width: '40%',
                                                        }}
                                                        value={userCount}
                                                        InputProps={{
                                                            inputProps: {
                                                                min: NODE_PARAMS_INTERVAL.ad['user_count'][0],
                                                                max: NODE_PARAMS_INTERVAL.ad['user_count'][1],
                                                            },
                                                        }}
                                                        onChange={handleUserCount}
                                                    />
                                                </Box>
                                                {(node.type === 'ad' || node.type === 'tech_tour_ad') && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'baseline',
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <Box>
                                                            <Tooltip
                                                                sx={{ mr: 1, transform: 'translateY(3px)' }}
                                                                title={t('ad_group_count_hint')}
                                                            >
                                                                <HelpOutlineIcon fontSize="small" />
                                                            </Tooltip>
                                                            {t('group_count')}:
                                                        </Box>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            sx={{
                                                                mt: 2,
                                                                backgroundColor:
                                                                    theme.palette.mode === 'light'
                                                                        ? 'white'
                                                                        : 'transparent',
                                                                width: '40%',
                                                            }}
                                                            value={groupCount}
                                                            InputProps={{
                                                                inputProps: {
                                                                    min: NODE_PARAMS_INTERVAL.ad['group_count'][0],
                                                                    max: Math.max(
                                                                        NODE_PARAMS_INTERVAL.ad['group_count'][1],
                                                                        userCount,
                                                                    ),
                                                                },
                                                            }}
                                                            onChange={handleGroupCount}
                                                        />
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                                {node.type === 'icap' && (
                                    <TextField
                                        label={t('servers-count')}
                                        size="small"
                                        type="number"
                                        sx={{
                                            backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                                            width: '50%',
                                        }}
                                        value={serversCount}
                                        onChange={handleServersCountChange}
                                        InputProps={{
                                            inputProps: {
                                                min: NODE_PARAMS_INTERVAL.icap.serversCount[0],
                                                max: NODE_PARAMS_INTERVAL.icap.serversCount[1],
                                            },
                                        }}
                                        disabled={ipsReadonly}
                                    />
                                )}
                                {node.type === 'pppoeserver' && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label={t('ips-pool-size')}
                                            size="small"
                                            type="number"
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.mode === 'light' ? 'white' : 'transparent',
                                                width: '50%',
                                            }}
                                            value={ipsPoolSize}
                                            onChange={handleIpsPoolSizeChange}
                                            InputProps={{
                                                inputProps: {
                                                    min: NODE_PARAMS_INTERVAL.pppoeserver['ips-pool-size'][0],
                                                    max: NODE_PARAMS_INTERVAL.pppoeserver['ips-pool-size'][1],
                                                },
                                            }}
                                            disabled={ipsReadonly}
                                        />
                                        <FormControl
                                            size="small"
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.mode === 'light' ? 'white' : 'transparent',
                                                width: '50%',
                                            }}
                                            disabled={ipsReadonly}
                                        >
                                            <InputLabel id="auth-type-label">{t('auth-type')}</InputLabel>
                                            <Select
                                                labelId="auth-type-label"
                                                value={authType}
                                                label={t('auth-type')}
                                                onChange={handleAuthTypeChange}
                                            >
                                                <MenuItem value={'chap'}>chap</MenuItem>
                                                <MenuItem value={'pap'}>pap</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                                {PPPOE_BASED_NODES_LIST.includes(node.type) && node.type !== 'pppoeserver' && (
                                    <TextField
                                        label={t('users-pool-size')}
                                        size="small"
                                        type="number"
                                        sx={{
                                            backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                                            width: '50%',
                                        }}
                                        value={usersPoolSize}
                                        onChange={handleUsersPoolSizeChange}
                                        InputProps={{
                                            inputProps: {
                                                min: NODE_PARAMS_INTERVAL[node.type]['users-pool-size'][0],
                                                max: NODE_PARAMS_INTERVAL[node.type]['users-pool-size'][1],
                                            },
                                        }}
                                        disabled={ipsReadonly}
                                    />
                                )}
                            </Box>
                        </TabPanel>
                    )}

                    <TabPanel value="words" sx={{ p: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                p: '16px 8px',
                            }}
                        >
                            <Box
                                component={'form'}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                                onSubmit={handleSubmitWordsInputValue}
                            >
                                <TextField
                                    placeholder={t('enter') + ' ' + t('word') + '...'}
                                    size="small"
                                    value={wordsInputValue}
                                    onChange={(e) => setWordsInputValue(e.target.value)}
                                    InputProps={{
                                        sx: {
                                            height: 32,
                                        },
                                    }}
                                />
                                <UtmsButton
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        color: '#1890ff',
                                        borderColor: '#1890ff',
                                        '&:hover': {
                                            color: '#1890ff',
                                            borderColor: '#1890ff',
                                        },
                                        height: 32,
                                    }}
                                    disabled={!wordsInputValue.length}
                                    type="submit"
                                >
                                    OK
                                </UtmsButton>
                            </Box>
                            <List
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    p: 0,
                                    overflowY: 'auto',
                                    maxHeight: 192,
                                    scrollbarGutter: 'stable',
                                }}
                            >
                                {words &&
                                    words.map((item, index) => {
                                        return (
                                            <ListItem
                                                sx={{
                                                    justifyContent: 'space-between',
                                                    p: '2px 16px',
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Typography
                                                    component={'p'}
                                                    sx={{
                                                        maxWidth: 234,
                                                        wordBreak: 'break-word', // Перенос по словам или символам
                                                    }}
                                                >
                                                    {item}
                                                </Typography>
                                                <IconButton
                                                    onClick={() => handleDeleteWord(index)}
                                                    color="error"
                                                    sx={{ height: 32, width: 32 }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItem>
                                        );
                                    })}
                            </List>
                        </Box>
                    </TabPanel>
                    {(node.type === 'trex' ||
                        node.type === 'cisco' ||
                        node.type === 'windows' ||
                        node.type === 'encryptor' ||
                        node.type === 'ad' ||
                        node.type === 'tech_tour_ad') && (
                        <TabPanel value="config" sx={{ p: 0 }}>
                            {(node.type === 'cisco' ||
                                node.type === 'windows' ||
                                node.type === 'ad' ||
                                node.type === 'tech_tour_ad' ||
                                node.type === 'encryptor') && (
                                <>
                                    <FileInput
                                        onFileSelect={handleChangeConfig}
                                        defaultValue={configName}
                                        sx={{ mt: 2, mb: 2 }}
                                    />
                                    <TextField
                                        multiline
                                        rows={8}
                                        onChange={(e) => setConfig(e.target.value)}
                                        value={config}
                                        fullWidth
                                    />
                                </>
                            )}
                            {node.type === 'trex' && (
                                <TrexConfigForm trexConfig={trexConfig} setTrexConfig={setTrexConfig} />
                            )}
                        </TabPanel>
                    )}
                </TabContext>
            </Box>
        </StyledMenu>
    );
};

const PortItem = ({ port, handleRemovePort, withStaticPorts, configureOnly, ipsReadonly, nodeId }) => {
    // const [isIpsReadonly, setIsIpsReadonly] = useState(ipsReadonly === undefined || ipsReadonly);
    // const [ipsValue, setIpsValue] = useState(port['ips']);
    const [ipAddressValue, setIpAddressValue] = useState(port['ip_address']);
    const [ipAddressInputError, setIpAddressInputError] = useState(false);
    // const inputRef = useRef(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const edges = useEdges();
    const { getNode } = useReactFlow();

    const isPortConnectedToZoneWithNetwork = useMemo(() => {
        return edges.some((edge) => {
            const connectedZoneNode = getNode(edge.target);

            if (!connectedZoneNode) {
                return false;
            }

            const isEdgeForThisPort = edge.sourceHandle === port.name && edge.source === nodeId;

            return isEdgeForThisPort && Boolean(connectedZoneNode.data.network);
        });
    }, [edges, getNode, nodeId, port.name]);

    const ipMustBeAssigned = isPortConnectedToZoneWithNetwork && !port['ip_address'];

    // // save new value to "data" object
    // useEffect(() => {
    //     port['ips'] = ipsValue;
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [ipsValue]);

    // const handleIpsChange = (e) => {
    //     const inputEl = e.target;
    //     enforceMinMax(inputEl);
    //
    //     setIpsValue(Number(inputEl.value));
    // };

    const handleChangePortIp = (e) => {
        const value = e.target.value;
        setIpAddressValue(value);
    };

    const handlePortIpInputBlur = (e) => {
        const value = e.target.value;

        if (value && !matchesIpRegex(value, 'subnet')) {
            setIpAddressInputError(true);
            return;
        }

        setIpAddressInputError(false);

        if (value) {
            port['ip_address'] = value;
        } else {
            delete port['ip_address'];
        }
    };

    return (
        <ListItem
            sx={{
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0,
                '& .MuiListItemSecondaryAction-root': {
                    top: '24px',
                },
            }}
            secondaryAction={
                withStaticPorts ? undefined : (
                    <HtmlTooltip placement="right" title={t('delete_port')}>
                        <IconButton
                            disabled={configureOnly}
                            sx={{ visibility: port['name'] === 'port0' ? 'hidden' : '' }}
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleRemovePort(port['name'])}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </HtmlTooltip>
                )
            }
        >
            <Box sx={{ display: 'flex', gap: 2, flex: 1, alignItems: 'flex-start' }}>
                <Chip label={port['name']} sx={{ fontWeight: 900, borderRadius: '8px', mt: 1 }} />
                <Tooltip
                    title={
                        isPortConnectedToZoneWithNetwork
                            ? ''
                            : 'You can assign IP only if port is connected to zone with specified "Network" parameter'
                    }
                >
                    <TextField
                        margin="dense"
                        placeholder="Ex. 192.168.70.0/24"
                        label={t('ip_address')}
                        disabled={!isPortConnectedToZoneWithNetwork || configureOnly}
                        size="small"
                        sx={{
                            backgroundColor: theme.palette.mode === 'light' ? 'white' : 'transparent',
                            width: 140,
                            '& .MuiFormHelperText-root': {
                                background: 'var(--custom-textbox-background-color)',
                                margin: 0,
                            },
                            opacity: !isPortConnectedToZoneWithNetwork ? 0.5 : 1,
                        }}
                        value={ipAddressValue}
                        onChange={handleChangePortIp}
                        onBlur={handlePortIpInputBlur}
                        error={ipAddressInputError || ipMustBeAssigned}
                        helperText={
                            ipMustBeAssigned
                                ? t('ip_address_is_required')
                                : ipAddressInputError && t('ip_address_helper_text')
                        }
                    />
                </Tooltip>
            </Box>

            {/*{port['ips'] !== undefined && (*/}
            {/*    <ListItemText*/}
            {/*        onDoubleClick={() => setIsIpsReadonly(ipsReadonly === undefined || ipsReadonly)} */}
            {/*        onBlur={() => setIsIpsReadonly(true)}*/}
            {/*        primaryTypographyProps={{ display: 'flex', justifyContent: 'center', position: 'relative' }}*/}
            {/*        primary={*/}
            {/*            <>*/}
            {/*                <Chip*/}
            {/*                    label={*/}
            {/*                        <input*/}
            {/*                            ref={inputRef}*/}
            {/*                            type="number"*/}
            {/*                            readOnly={isIpsReadonly}*/}
            {/*                            value={ipsValue}*/}
            {/*                            onChange={handleIpsChange}*/}
            {/*                            min={MIN_IPS_NUMBER_FOR_PORT}*/}
            {/*                            max={MAX_IPS_NUMBER_FOR_PORT}*/}
            {/*                            className={styles['unset-input']}*/}
            {/*                        />*/}
            {/*                    }*/}
            {/*                    sx={{*/}
            {/*                        boxSizing: 'border-box',*/}
            {/*                        fontWeight: 900,*/}
            {/*                        borderRadius: '8px',*/}
            {/*                        border: !isIpsReadonly ? '1px solid var(--antd-primary-color)' : '',*/}
            {/*                    }}*/}
            {/*                />*/}
            {/*            </>*/}
            {/*        }*/}
            {/*    />*/}
            {/*)}*/}
        </ListItem>
    );
};

export const NodeToolbar = ({
    nodeId,
    handleAddPortButton,
    handleDeletePortButton,
    hidden,
    HANDLES_POOL,
    NodeConfigMenuProps,
    ...restProps
}) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);
    const menuOpenRef = useRef();
    const { getNode } = useReactFlow();
    const node = useMemo(() => getNode(nodeId), [nodeId, getNode]);

    const { handleDeleteNode } = useDeleteNode();

    const handleOpenConfigMenu = (event) => {
        event.preventDefault();
        setMenuAnchorEl(menuOpenRef.current);
    };

    return (
        <>
            <Box sx={{ position: 'absolute', top: -32, right: -40, cursor: 'pointer', display: 'flex', gap: '4px' }}>
                <UtmsButton
                    ref={menuOpenRef}
                    disabled={menuOpen}
                    size="small"
                    sx={{
                        visibility: hidden ? 'hidden' : '',
                        minWidth: '13px',
                        minHeight: '13px',
                        borderRadius: 1,
                        padding: 0,
                    }}
                    variant="contained"
                    color="info"
                    onClick={handleOpenConfigMenu}
                    {...restProps}
                >
                    <SettingsIcon
                        sx={{
                            width: '1.2em',
                            height: '1.2em',
                            fontSize: 9,
                            transform: menuOpen ? 'rotate(90deg)' : '',
                            transition: 'transform 0.3s ease-in',
                        }}
                    />
                </UtmsButton>
                {node.deletable !== false && (
                    <UtmsButton
                        size="small"
                        className="delete-node-btn"
                        sx={{
                            visibility: hidden ? 'hidden' : '',
                            minWidth: '13px',
                            minHeight: '13px',
                            borderRadius: 1,
                            padding: 0,
                        }}
                        variant="contained"
                        color="error"
                        onClick={(_e) => handleDeleteNode(node, 'button')}
                        {...restProps}
                    >
                        <DeleteOutlineIcon color="white" sx={{ fontSize: 9, width: '1.25em', height: '1.25em' }} />
                    </UtmsButton>
                )}
            </Box>
            <NodeConfigMenu
                anchorEl={menuAnchorEl}
                setAnchorEl={setMenuAnchorEl}
                nodeId={nodeId}
                addNewPort={handleAddPortButton}
                removePort={handleDeletePortButton}
                handlesPool={HANDLES_POOL}
                {...NodeConfigMenuProps}
            />
        </>
    );
};

export default NodeToolbar;
